import { get, set, setProperties } from "@ember/object"
import { guidFor } from "@ember/object/internals"
import Service, { inject as service } from "@ember/service"
import { dasherize } from "@ember/string"
import { isNone } from "@ember/utils"
import { migrateFromCardsInPlace } from "@shared/lib/card-stack/unified-migration"
import type { PatchModel } from "@shared/models/patch"
import type { TypeToModel } from "@shared/models/type-to-model"
import { patchDocument } from "cc-frontend/lib/ember-patch"
import {
  createInsertContext,
  type DocumentInsertContext,
  type DocumentInsertedListener,
  type JsonApiDocument,
} from "../lib/document-backends/types"
import type PatchCoordinator from "./patch-coordinator"

/**
 * The internal Ember-side store. It owns the in-memory document store
 * and is the backend that
 * the public `ember-store` facade reads from and writes to. Application
 * components should talk to the `ember-store` facade, not this service.
 */
export default class InternalEmberStore extends Service {
  @service declare patchCoordinator: PatchCoordinator

  __store__: object = Object.create(null)

  private readonly afterInsertListeners = new Set<DocumentInsertedListener>()

  /**
   * Runs immediately when the service is created. Exposes the in-memory store on
   * the window for debugging and makes sure it's empty.
   */
  constructor(...args) {
    super(...args)
    // @ts-ignore
    window.__STORE__ = this.__store__

    this.__store__ = Object.create(null)
  }

  get rawStore() {
    return this.__store__
  }

  hasDocument(type: keyof TypeToModel, id: string): boolean {
    return !!this.findInMemory(type, id)
  }

  getRevision(type: keyof TypeToModel, id: string): number {
    const model = this.findInMemory(type, id)
    return get(model, "attributes._revision") || 0
  }

  documentsForType(type: keyof TypeToModel): Record<string, any> {
    return this.rawStore[type]
  }

  insertDocument(
    doc: JsonApiDocument,
    context: DocumentInsertContext = createInsertContext("manual")
  ): void {
    // Model-specific migrations. Mirrored in InternalSupergrainStore's prepareInsert.
    if (doc.type === "card-stack") migrateFromCardsInPlace(doc as any)

    const existingDoc = this.findInMemory(doc.type as keyof TypeToModel, doc.id)
    if (this.hasNewerRevision(existingDoc, doc)) return

    if (doc.meta === undefined) {
      set(doc, "meta", {})
    }

    if (context.origin !== "replication") {
      // If we're overwriting an existing doc (e.g. from checkStaleness), we don't
      // know which optimistic local updates are included in the server's new version.
      // It seems safest, and reasonably likely, to assume that all of them are included.
      this.patchCoordinator.clearQueue(doc.type as keyof TypeToModel, doc.id)
    }

    const insertedDoc = this.insert(doc) ?? doc
    this.emitAfterInsert(insertedDoc, context)
  }

  onAfterInsert(listener: DocumentInsertedListener): () => void {
    this.afterInsertListeners.add(listener)
    return () => this.afterInsertListeners.delete(listener)
  }

  canStore(_type: string): boolean {
    return true
  }

  /**
   * Returns the requested document from local memory, if present.
   */
  findInMemory(type: keyof TypeToModel, id: string): any {
    // @ts-ignore
    return get(this, `__store__.${dasherize(type)}.${id}`)
  }

  /**
   * Resets/clears the in-memory store.
   */
  reset(): void {
    set(this, "__store__", Object.create(null))
  }

  /**
   * Checks if the patch in the argument is in the store's local memory. Makes sure the patch exists
   * and has the right info, creating the model type / document if necessary, then applies the patch
   * and ups the internal version.
   *
   * @param  patch PatchModel - references model type, id, actionId
   * @returns A function that rewinds the patch (updating our store's model in-place), assuming
   * that the model is in its exact state after applying the patch.
   */
  patch(patch: PatchModel) {
    if (patch === undefined)
      throw Error(
        "The patch function received undefined. Likely, this is because you didn't return the patch from the patch function."
      )
    let id = patch.document.id

    if (isNone(id) || id === "") {
      console.error("Erroneous Patch", patch)
      throw Error("No ID given. Cannot process.")
    }

    // @ts-ignore
    if (isNone(get(this, `__store__.${patch.document.modelType}`))) {
      // @ts-ignore
      set(this, `__store__.${patch.document.modelType}`, {})
    }

    // @ts-ignore
    if (isNone(get(this, `__store__.${patch.document.modelType}.${patch.document.id}`))) {
      // @ts-ignore
      set(this, `__store__.${patch.document.modelType}.${patch.document.id}`, {
        type: patch.document.modelType,
        id: patch.document.id,
        attributes: {},
        meta: {},
      })
    }

    // @ts-ignore
    let doc = get(this, `__store__.${patch.document.modelType}.${patch.document.id}`) as any
    return patchDocument(doc, patch)
  }

  /**
   * Keeps local storage current by adding previously unavailable models retrieved through fetch calls
   * or updating existing models with new updates from the server.
   *
   * @param  doc [object] - document object
   * @return     [object] - updated (or new) document object
   */
  insert(doc) {
    if (get(this, "isDestroyed") || get(this, "isDestroying")) return

    // If we're inserting nothing, return nothing
    if (doc === undefined) return null

    // Make sure we have a hash for this type of model
    if (isNone(this.__store__[doc.type])) this.__store__[doc.type] = Object.create(null)

    // get the document
    // @ts-ignore
    let existingDoc = get(this, `__store__.${doc.type}.${doc.id}`)

    // If there is an existing doc, update it with the fresh data from the server
    if (existingDoc !== undefined && existingDoc !== null) {
      setProperties(existingDoc, doc)
      // @ts-ignore
      let existingInternalVersion = existingDoc?.internalVersion || 0
      // @ts-ignore
      set(existingDoc, "internalVersion", existingInternalVersion + 1)
      return existingDoc

      // if there isn't an existing doc and we actually are dealing with a doc, add it to the store
      // and then return it
    } else if (doc && doc.type && doc.id) {
      // Probably not necessary but can make debugging easier to see if
      // it's the same object. This can probably be taken out.
      guidFor(doc)
      guidFor(doc.attributes)
      // Set an internal version for tracking
      doc.internalVersion = 0
      // @ts-ignore
      set(this, `__store__.${doc.type}.${doc.id}`, doc)
      return doc
    }
  }

  private emitAfterInsert(doc: JsonApiDocument, context: DocumentInsertContext): void {
    for (const listener of this.afterInsertListeners) {
      listener({ backendId: "ember", doc, context })
    }
  }

  private hasNewerRevision(existingDoc: unknown, incomingDoc: JsonApiDocument): boolean {
    if (existingDoc == null) return false

    const existingRevision = get(existingDoc as object, "attributes._revision")
    const incomingRevision = incomingDoc.attributes?._revision
    return (
      typeof existingRevision === "number" &&
      typeof incomingRevision === "number" &&
      existingRevision > incomingRevision
    )
  }
}

declare module "@ember/service" {
  interface Registry {
    "internal-ember-store": InternalEmberStore
  }
}
